import React from "react";
import {
  FaShoppingCart,
  FaTruck,
  FaUndoAlt,
  FaShieldAlt,
  // FaHeadset,
  // FaLeaf,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

const ProcessSteps = ({ items = null, title = null, variant = "default" }) => {
  const { t } = useTranslation();
  const isWallarts = variant === "wallarts";

  // Default items if none provided
  const defaultItems = [
    {
      icon: FaShoppingCart,
      title: t("processSteps.items.ordering.title", "Easy Ordering"),
      description: t(
        "processSteps.items.ordering.description",
        "Browse and order your favorite products with just a few clicks. Simple and secure checkout process.",
      ),
    },
    {
      icon: FaTruck,
      title: t("processSteps.items.shipping.title", "Fast Shipping"),
      description: t(
        "processSteps.items.shipping.description",
        "We ship your orders quickly and securely. Track your package in real-time from warehouse to doorstep.",
      ),
    },
    {
      icon: FaUndoAlt,
      title: t("processSteps.items.returns.title", "Easy Returns"),
      description: t(
        "processSteps.items.returns.description",
        "Not satisfied? Return or exchange within 30 days for a full refund. Hassle-free return process.",
      ),
    },
    {
      icon: FaShieldAlt,
      title: t("processSteps.items.payment.title", "Secure Payment"),
      description: t(
        "processSteps.items.payment.description",
        "Your payment information is encrypted and secure. We protect your data with industry-standard security.",
      ),
    },
  ];

  // Use provided items or defaults
  const processItems = items || defaultItems;
  const sectionTitle =
    title || t("processSteps.title", "Why Shop With CrossBorder?");

  return (
    <section
      className={
        isWallarts
          ? "relative overflow-hidden bg-gradient-to-br from-[#f7f3ef] via-[#e6ded7] to-[#f3e9e0] px-4 py-14 sm:px-6 lg:px-10"
          : "canvas-background pt-0 lg:pt-4 md:py-16 px-4 sm:px-6 lg:px-10 lg:py-8"
      }
    >
      {isWallarts && (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[rgba(199,162,155,0.28)] to-transparent" />
      )}
      <div className="mx-10 mx-auto">
        {/* Section Title */}
        <div className="text-center mb-12">
          {isWallarts && (
            <span className="hidden inline-flex items-center rounded-full border border-[#d7c4b8] bg-white/55 px-4 py-1.5 text-[0.72rem] tracking-[0.26em] uppercase text-[#7a5c3e] backdrop-blur-sm"></span>
          )}
          <h2
            className={
              isWallarts
                ? "mt-4 text-3xl md:text-4xl font-semibold tracking-[-0.02em] text-[#8f6f58] mb-4 font-raleway"
                : "md:text-3xl text-[1.2rem] font-bold text-[#83375B] mb-4"
            }
          >
            {sectionTitle}
          </h2>
          {isWallarts && (
            <p className="mx-auto max-w-3xl text-base leading-7 text-[#8d735f] md:text-lg font-raleway">
              {t(
                "wallartsPage.processIntro",
                "A calm shopping experience, carefully packed pieces, and details that make your wall art feel worth keeping.",
              )}
            </p>
          )}
        </div>

        {/* Process Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {processItems.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <div
                key={index}
                className={
                  isWallarts
                    ? "group relative rounded-[1.75rem] border border-[rgba(191,174,158,0.35)] bg-white/68 p-6 text-center shadow-[0_20px_50px_rgba(122,92,62,0.08)] transition-all duration-300 ease-in-out hover:-translate-y-2 hover:border-[#c9b2a1] hover:shadow-[0_28px_60px_rgba(122,92,62,0.14)] md:text-left"
                    : "group relative bg-[rgba(255,255,255,0.5)] rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 ease-in-out transform hover:-translate-y-2 p-6 border border-gray-100 hover:border-[#AA7F96] text-center md:text-left"
                }
              >
                {/* Accent top border on hover */}
                <div
                  className={
                    isWallarts
                      ? "absolute left-5 right-5 top-0 h-1 rounded-b-full bg-gradient-to-r from-[#d6c0af] to-[#8f6f58] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                      : "absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#AA7F96] to-[#83375B] rounded-t-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  }
                ></div>

                {/* Step Number */}
                <div
                  className={
                    isWallarts
                      ? "absolute -top-3 left-1/2 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full bg-[#b79d8a] text-sm font-bold text-white md:left-auto md:-left-3 md:translate-x-0"
                      : "absolute -top-3 left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:-left-3 w-8 h-8 bg-[#AA7F96] text-white rounded-full flex items-center justify-center font-bold text-sm"
                  }
                >
                  {index + 1}
                </div>

                {/* Icon Container */}
                <div
                  className={
                    isWallarts
                      ? "mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-[1.25rem] bg-gradient-to-br from-[#efe3d9] to-[#f9f5f1] transition-all duration-300 group-hover:from-[#b79d8a] group-hover:to-[#8f6f58] md:mx-0"
                      : "bg-gradient-to-br from-[#AA7F96]/10 to-[#83375B]/10 rounded-lg w-16 h-16 flex items-center justify-center mb-4 mx-auto md:mx-0 group-hover:from-[#AA7F96] group-hover:to-[#83375B] transition-all duration-300"
                  }
                >
                  <IconComponent
                    className={
                      isWallarts
                        ? "text-2xl text-[#8f6f58] transition-colors duration-300 group-hover:text-white"
                        : "text-2xl text-[#83375B] group-hover:text-white transition-colors duration-300"
                    }
                  />
                </div>

                {/* Title */}
                <h3
                  className={
                    isWallarts
                      ? "mb-3 text-lg font-semibold text-[#7a5c3e] transition-colors duration-300 group-hover:text-[#a58369]"
                      : "text-lg font-bold text-[#83375B] mb-3 group-hover:text-[#AA7F96] transition-colors duration-300"
                  }
                >
                  {item.title}
                </h3>

                {/* Description */}
                <p
                  className={
                    isWallarts
                      ? "text-sm leading-relaxed text-[#8d735f]"
                      : "text-gray-600 text-sm leading-relaxed"
                  }
                >
                  {item.description}
                </p>

                {/* Bottom accent */}
                <div
                  className={
                    isWallarts
                      ? "mt-4 h-0.5 w-0 bg-gradient-to-r from-[#d6c0af] to-[#8f6f58] transition-all duration-300 group-hover:w-full"
                      : "mt-4 h-0.5 w-0 group-hover:w-full bg-gradient-to-r from-[#AA7F96] to-[#83375B] transition-all duration-300"
                  }
                ></div>
              </div>
            );
          })}
        </div>

        {/* Optional: Decorative elements */}
        <div className="mt-10 text-center">
          <p
            className={
              isWallarts ? "text-sm text-[#8d735f]" : "text-gray-500 text-sm"
            }
          >
            {t(
              "processSteps.trustedText",
              "✓ Trusted by thousands of satisfied customers worldwide",
            )}
          </p>
        </div>
      </div>
    </section>
  );
};

export default ProcessSteps;
